import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import {
    Download,
    Upload,
    Trash2,
    FileText,
    CheckCircle2,
    AlertCircle,
    Loader2,
    RefreshCw,
    HardDrive
} from 'lucide-react';

interface ReleaseFile {
    name: string;
    size: number;
    modifiedAt: string;
}

export function DesktopReleaseManagement() {
    const { getAuthHeader, hasModuleAccess } = useApp();
    const [files, setFiles] = useState<ReleaseFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const apiBase = (import.meta as any).env?.VITE_API_BASE_URL || '';

    const fetchReleases = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${apiBase}/api/admin/desktop-releases`, {
                headers: getAuthHeader()
            });
            if (!res.ok) throw new Error('Failed to fetch releases');
            const data = await res.json();
            setFiles(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReleases();
    }, []);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files;
        if (!selectedFiles || selectedFiles.length === 0) return;

        setUploading(true);
        setError(null);
        setSuccessMessage(null);

        const formData = new FormData();
        Array.from(selectedFiles).forEach(file => {
            formData.append('files', file);
        });

        try {
            const res = await fetch(`${apiBase}/api/admin/desktop-releases`, {
                method: 'POST',
                headers: getAuthHeader(),
                body: formData
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Upload failed');
            }

            setSuccessMessage(`Successfully uploaded ${selectedFiles.length} file(s)`);
            fetchReleases();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setUploading(false);
            // Reset input
            e.target.value = '';
        }
    };

    const clearReleases = async () => {
        if (!confirm('Are you sure you want to delete all release files? This will prevent users from updating until you upload new ones.')) return;

        setError(null);
        try {
            const res = await fetch(`${apiBase}/api/admin/desktop-releases`, {
                method: 'DELETE',
                headers: getAuthHeader()
            });

            if (!res.ok) throw new Error('Failed to clear releases');

            setSuccessMessage('Successfully cleared all release files');
            fetchReleases();
        } catch (err: any) {
            setError(err.message);
        }
    };

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (!hasModuleAccess('desktop_releases')) {
        return <div className="p-8 text-center text-gray-500">You do not have permission to manage desktop releases.</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px 0' }}>Desktop App Releases</h2>
                    <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                        Manage automatic update packages for Tock Desktop.
                    </p>
                </div>
                <button
                    onClick={fetchReleases}
                    className="btn btn-ghost"
                    style={{ padding: 8 }}
                    disabled={loading}
                    title="Refresh list"
                >
                    <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                </button>
            </div>

            {/* Status Messages */}
            {error && (
                <div style={{
                    padding: '12px 16px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    borderRadius: 8,
                    color: '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 14
                }}>
                    <AlertCircle size={18} />
                    {error}
                </div>
            )}

            {successMessage && (
                <div style={{
                    padding: '12px 16px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    borderRadius: 8,
                    color: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 14
                }}>
                    <CheckCircle2 size={18} />
                    {successMessage}
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
                {/* File List */}
                <div style={{
                    background: 'var(--bg-secondary)',
                    borderRadius: 12,
                    border: '1px solid var(--border-color)',
                    overflow: 'hidden'
                }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Active Release Files</h3>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{files.length} files found</span>
                    </div>

                    <div style={{ minHeight: 300 }}>
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, color: 'var(--text-muted)' }}>
                                <Loader2 className="animate-spin" size={32} />
                            </div>
                        ) : files.length === 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 300, color: 'var(--text-muted)', gap: 12 }}>
                                <Download size={48} strokeWidth={1} style={{ opacity: 0.3 }} />
                                <p style={{ fontSize: 14 }}>No release files found on server.</p>
                            </div>
                        ) : (
                            <div style={{ padding: 12 }}>
                                {files.map(file => (
                                    <div
                                        key={file.name}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '12px 16px',
                                            borderRadius: 8,
                                            transition: 'background 0.2s',
                                            cursor: 'default'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <div style={{
                                            width: 40, height: 40, borderRadius: 8,
                                            background: 'var(--bg-primary)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            marginRight: 16, border: '1px solid var(--border-color)',
                                            color: file.name.endsWith('.yml') ? '#6366f1' : '#10b981'
                                        }}>
                                            {file.name.endsWith('.yml') ? <FileText size={20} /> : <HardDrive size={20} />}
                                        </div>
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {file.name}
                                            </div>
                                            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                                {formatSize(file.size)} • Modified {new Date(file.modifiedAt).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Upload Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div style={{
                        background: 'var(--bg-secondary)',
                        borderRadius: 12,
                        border: '1px solid var(--border-color)',
                        padding: 24,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}>
                        <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Upload size={18} color="var(--accent-primary)" />
                            Deploy New Version
                        </h3>
                        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                            Upload the <code style={{ background: 'var(--bg-tertiary)', padding: '2px 4px', borderRadius: 4 }}>latest.yml</code> and <code style={{ background: 'var(--bg-tertiary)', padding: '2px 4px', borderRadius: 4 }}>.exe</code> files from your <code style={{ color: 'var(--text-primary)' }}>dist-electron</code> folder.
                        </p>

                        <label
                            className={`btn ${uploading ? 'btn-disabled' : 'btn-primary'}`}
                            style={{
                                cursor: uploading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 10,
                                padding: '12px 16px'
                            }}
                        >
                            {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                            {uploading ? 'Uploading...' : 'Select Files to Upload'}
                            <input
                                type="file"
                                multiple
                                onChange={handleFileUpload}
                                disabled={uploading}
                                style={{ display: 'none' }}
                                accept=".exe,.yml,.blockmap"
                            />
                        </label>

                        <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
                            Max file size: 1GB per file
                        </div>
                    </div>

                    <div style={{
                        background: 'rgba(239, 68, 68, 0.05)',
                        borderRadius: 12,
                        border: '1px solid rgba(239, 68, 68, 0.1)',
                        padding: 20,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12
                    }}>
                        <h4 style={{ fontSize: 14, fontWeight: 600, color: '#ef4444', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Trash2 size={16} />
                            Danger Zone
                        </h4>
                        <p style={{ fontSize: 12, color: 'rgba(239, 68, 68, 0.8)', margin: 0 }}>
                            Clearing releases will remove all current installers from the server. Users will not be able to auto-update.
                        </p>
                        <button
                            onClick={clearReleases}
                            className="btn"
                            style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                fontSize: 13,
                                fontWeight: 600
                            }}
                        >
                            Clear Releases Folder
                        </button>
                    </div>

                    <div style={{
                        background: 'var(--bg-secondary)',
                        borderRadius: 12,
                        border: '1px solid var(--border-color)',
                        padding: 16,
                        display: 'flex',
                        gap: 12
                    }}>
                        <RefreshCw size={16} color="var(--accent-primary)" style={{ flexShrink: 0, marginTop: 2 }} />
                        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                            <strong>Update Check URL:</strong><br />
                            The desktop app should be configured to check:<br />
                            <code style={{ fontSize: 11, background: 'var(--bg-tertiary)', padding: '2px 4px', borderRadius: 4, display: 'inline-block', marginTop: 4 }}>
                                {apiBase}/releases/
                            </code>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
